import { FC, useState, useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import { Avatar, Badge, Dropdown, Layout, List, Menu, Popover, Select, Space, Typography } from 'antd'
import type { MenuProps } from 'antd'
import {
  TeamOutlined, CalendarOutlined, WalletOutlined, TransactionOutlined,
  IdcardOutlined, UserOutlined, ApartmentOutlined, MessageOutlined,
  BarChartOutlined, CheckSquareOutlined, RobotOutlined, ApiOutlined,
  KeyOutlined, SoundOutlined, SafetyCertificateOutlined, DashboardOutlined,
  ExperimentOutlined, UserAddOutlined, SettingOutlined, ToolOutlined,
  DatabaseOutlined, LogoutOutlined, HeartOutlined, StarOutlined, HistoryOutlined, ThunderboltOutlined,
  EnvironmentOutlined, FieldTimeOutlined, SearchOutlined, SwapOutlined, BellOutlined, CoffeeOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import { usePoolActivity } from '@/context/PoolActivityContext'
import { usePendingCounts } from '@/hooks/usePendingCounts'
import { CommandPalette } from '@/components/admin/CommandPalette'
import { AgentHarnessProvider } from '@/components/admin/harness/AgentHarnessContext'
import { AgentFloatWidget } from '@/components/admin/harness/AgentFloatWidget'

const { Header, Sider, Content } = Layout
const { Text } = Typography

interface Props {
  user?: { nickname?: string; phone?: string; email?: string }
  onLogout?: () => void
}

type MenuItem = Required<MenuProps>['items'][number]

const navGroups: Array<{
  key: string
  label: string
  icon: React.ReactNode
  children: Array<{ key: string; label: string; icon: React.ReactNode }>
}> = [
  {
    key: 'group-overview', label: '总览', icon: <DashboardOutlined />,
    children: [
      { key: '/dashboard', label: '业务总览', icon: <DashboardOutlined /> },
    ],
  },
  {
    key: 'group-agent', label: 'Agent', icon: <RobotOutlined />,
    children: [
      { key: '/agent-harness', label: 'Agent', icon: <ExperimentOutlined /> },
      { key: '/fs-docs', label: 'FS 文档管理', icon: <FileTextOutlined /> },
    ],
  },
  {
    key: 'group-business', label: '业务管理', icon: <TeamOutlined />,
    children: [
      { key: '/organizers', label: '组织者管理', icon: <TeamOutlined /> },
      { key: '/activities', label: '活动管理', icon: <CalendarOutlined /> },
      { key: '/pool/plans', label: '套餐管理', icon: <WalletOutlined /> },
      { key: '/pool/transactions', label: '交易记录', icon: <TransactionOutlined /> },
      { key: '/pool/venues', label: '场地管理', icon: <EnvironmentOutlined /> },
    ],
  },
  {
    key: 'group-profile', label: '对话与画像', icon: <IdcardOutlined />,
    children: [
      { key: '/pool/search', label: '用户搜索', icon: <SearchOutlined /> },
      { key: '/pool/profiles', label: '用户画像', icon: <IdcardOutlined /> },
      { key: '/pool/members', label: '池成员', icon: <UserOutlined /> },
    ],
  },
  {
    key: 'group-match', label: '匹配', icon: <ApartmentOutlined />,
    children: [
      { key: '/pool/user-match-stats', label: '用户匹配概览', icon: <BarChartOutlined /> },
      { key: '/pool/recommendations', label: '推荐记录', icon: <StarOutlined /> },
      { key: '/pool/push-logs', label: '推送记录', icon: <BellOutlined /> },
      { key: '/pool/match-logs', label: '匹配日志', icon: <HistoryOutlined /> },
      { key: '/pool/trigger-match', label: '手动触发', icon: <ThunderboltOutlined /> },
      { key: '/category-tree', label: '分类树', icon: <ApartmentOutlined /> },
    ],
  },
  {
    key: 'group-encounter', label: '见面安排', icon: <CoffeeOutlined />,
    children: [
      { key: '/pool/todo', label: '待办中心', icon: <CheckSquareOutlined /> },
      { key: '/pool/pairs', label: '匹配决策', icon: <SwapOutlined /> },
      { key: '/pool/encounters', label: '见面管理', icon: <HeartOutlined /> },
    ],
  },
  {
    key: 'group-ai-prompt', label: 'AI提示词管理', icon: <RobotOutlined />,
    children: [
      { key: '/chat-templates', label: '聊天模板', icon: <MessageOutlined /> },
      { key: '/analyze-templates', label: '分析模板', icon: <BarChartOutlined /> },
      { key: '/analyze-tasks', label: '分析任务', icon: <CheckSquareOutlined /> },
      { key: '/flow-controllers', label: '流程控制器', icon: <RobotOutlined /> },
      { key: '/controller-sessions', label: 'Controller会话', icon: <ApiOutlined /> },
    ],
  },
  {
    key: 'group-verify', label: '审核管理', icon: <SafetyCertificateOutlined />,
    children: [
      { key: '/edu-verify-review', label: '学历审核', icon: <SafetyCertificateOutlined /> },
    ],
  },
  {
    key: 'group-monitor', label: '监控与测试', icon: <DashboardOutlined />,
    children: [
      { key: '/queue-monitor', label: '队列监控', icon: <DashboardOutlined /> },
      { key: '/prompt-test', label: '提示词测试', icon: <ExperimentOutlined /> },
      { key: '/pool/cron-status', label: '定时任务', icon: <FieldTimeOutlined /> },
      { key: '/pool/seed-users', label: '造测试用户', icon: <UserAddOutlined /> },
    ],
  },
  {
    key: 'group-system', label: '系统配置', icon: <SettingOutlined />,
    children: [
      { key: '/system-configs', label: '系统配置', icon: <SettingOutlined /> },
    ],
  },
  {
    key: 'group-ai-base', label: 'AI基础配置', icon: <DatabaseOutlined />,
    children: [
      { key: '/ai-tools', label: 'Tools', icon: <ToolOutlined /> },
      { key: '/ai-connectors', label: 'Connectors', icon: <ApiOutlined /> },
      { key: '/ai-models', label: 'Models', icon: <DatabaseOutlined /> },
      { key: '/ai-providers', label: 'Providers', icon: <ApiOutlined /> },
      { key: '/ai-apikeys', label: 'API Keys', icon: <KeyOutlined /> },
      { key: '/tts-voices', label: '音色管理', icon: <SoundOutlined /> },
    ],
  },
]

const badgeCountsByKey: Record<string, 'eduVerifyPending' | 'queueFailed'> = {
  '/edu-verify-review': 'eduVerifyPending',
  '/queue-monitor': 'queueFailed',
}

function buildMenuItems(counts: { eduVerifyPending: number; queueFailed: number }): MenuItem[] {
  return navGroups.map(g => ({
    key: g.key,
    label: g.label,
    icon: g.icon,
    children: g.children.map(c => {
      const bk = badgeCountsByKey[c.key]
      const count = bk ? counts[bk] : 0
      return {
        key: c.key,
        icon: c.icon,
        label: count > 0 ? (
          <Space size={4}>
            {c.label}
            <Badge count={count} size="small" />
          </Space>
        ) : (
          c.label
        ),
      }
    }),
  }))
}

const leafKeys: string[] = navGroups.flatMap(g => g.children.map(c => c.key))

const OPEN_KEYS_STORAGE_KEY = 'jingque-admin:nav-open-keys'

function loadOpenKeys(): string[] {
  try {
    const raw = localStorage.getItem(OPEN_KEYS_STORAGE_KEY)
    if (raw) {
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) {
        return arr.filter((s): s is string => typeof s === 'string')
      }
    }
  } catch {
    /* noop */
  }
  return navGroups.map(g => g.key)
}

function findGroupKeyByPath(pathname: string): string | null {
  for (const g of navGroups) {
    for (const c of g.children) {
      if (pathname === c.key || pathname.startsWith(c.key + '/')) {
        return g.key
      }
    }
  }
  return null
}

function matchSelectedKey(pathname: string): string {
  const hit = leafKeys.find(k => pathname === k || pathname.startsWith(k + '/'))
  return hit || pathname
}

export const AdminLayout: FC<Props> = ({ user, onLogout }) => {
  const displayName = user?.nickname || user?.phone || user?.email || '管理员'
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { poolActivities, currentPoolActivityId, setCurrentPoolActivityId } = usePoolActivity()
  const isPoolRoute = pathname.startsWith('/pool/')

  const [openKeys, setOpenKeys] = useState<string[]>(() => loadOpenKeys())
  const [siderCollapsed, setSiderCollapsed] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const pending = usePendingCounts()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(v => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const palettePages = useMemo(
    () => navGroups.flatMap(g => g.children.map(c => ({ key: c.key, label: c.label, group: g.label }))),
    [],
  )

  const menuItems = useMemo(
    () => buildMenuItems({ eduVerifyPending: pending.eduVerifyPending, queueFailed: pending.queueFailed }),
    [pending.eduVerifyPending, pending.queueFailed],
  )

  const totalPending = pending.eduVerifyPending + pending.queueFailed + pending.cronStuck

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(openKeys))
    } catch {
      /* noop */
    }
  }, [openKeys])

  useEffect(() => {
    const gk = findGroupKeyByPath(pathname)
    if (gk && !openKeys.includes(gk)) {
      setOpenKeys(prev => [...prev, gk])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const selectedKeys = useMemo(() => [matchSelectedKey(pathname)], [pathname])

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      label: '登出',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: onLogout,
    },
  ]

  return (
    // Agent harness 状态提升到 Provider:整页路由与右下角悬浮弹窗共享同一会话/时间线/指标
    <AgentHarnessProvider>
    <Layout style={{ height: '100vh' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0',
          height: 56,
          lineHeight: 'normal',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Link to="/dashboard" style={{ color: 'inherit' }}>
          <Space size={12}>
            <img
              src="https://middleiman-public.oss-cn-hangzhou.aliyuncs.com/jingque_avatar.webp"
              alt="菁鹊"
              style={{ width: 32, height: 32, borderRadius: '50%' }}
            />
            <Text strong style={{ fontSize: 16 }}>菁鹊管理后台</Text>
          </Space>
        </Link>
        <Space size="middle">
          {isPoolRoute && poolActivities.length > 0 && (
            <Space size={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>当前池子</Text>
              <Select
                value={currentPoolActivityId || undefined}
                onChange={setCurrentPoolActivityId}
                style={{ minWidth: 220 }}
                showSearch
                optionFilterProp="label"
                options={poolActivities.map(a => ({
                  value: a.id,
                  label: `${a.name}${a.status === 'closed' ? ' (已关闭)' : ''}`,
                }))}
              />
            </Space>
          )}
          <Space
            size={6}
            onClick={() => navigate('/agent-harness')}
            style={{
              cursor: 'pointer',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: '3px 10px',
              color: 'rgba(0,0,0,0.45)',
            }}
          >
            <RobotOutlined style={{ fontSize: 13 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Agent</Text>
          </Space>
          <Space
            size={6}
            onClick={() => setPaletteOpen(true)}
            style={{
              cursor: 'pointer',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: '3px 10px',
              color: 'rgba(0,0,0,0.45)',
            }}
          >
            <SearchOutlined style={{ fontSize: 13 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>搜索</Text>
            <Text keyboard style={{ fontSize: 11 }}>⌘K</Text>
          </Space>
          <Popover
            placement="bottomRight"
            trigger="click"            content={
              <List
                size="small"
                style={{ width: 280 }}
                dataSource={[
                  {
                    to: '/edu-verify-review',
                    label: '学历审核待处理',
                    count: pending.eduVerifyPending,
                  },
                  {
                    to: '/queue-monitor',
                    label: '队列失败任务',
                    count: pending.queueFailed,
                  },
                  {
                    to: '/queue-monitor',
                    label: '队列积压(pending+retry)',
                    count: pending.queueBacklog,
                  },
                  {
                    to: '/pool/cron-status',
                    label: '定时任务疑似卡死',
                    count: pending.cronStuck,
                  },
                ]}
                renderItem={item => (
                  <List.Item
                    style={{ cursor: 'pointer', padding: '8px 4px' }}
                    onClick={() => navigate(item.to)}
                  >
                    <Text>{item.label}</Text>
                    <Badge
                      count={item.count}
                      size="small"
                      color={item.count > 0 ? '#cf1322' : '#d9d9d9'}
                    />
                  </List.Item>
                )}
              />
            }
          >
            <Badge count={totalPending} size="small" offset={[-2, 2]}>
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>
          </Popover>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }} size={8}>
              <Avatar size="small" style={{ background: '#6366f1' }}>
                {displayName.charAt(0)}
              </Avatar>
              <Text>{displayName}</Text>
            </Space>
          </Dropdown>
        </Space>
      </Header>

      <Layout style={{ minHeight: 0 }}>
        <Sider
          collapsible
          collapsed={siderCollapsed}
          onCollapse={setSiderCollapsed}
          width={224}
          theme="light"
          style={{
            borderRight: '1px solid #f0f0f0',
            height: 'calc(100vh - 56px)',
            position: 'sticky',
            top: 56,
            left: 0,
          }}
        >
          {/* 滚动区独立,底部预留 48px 给 Sider trigger(收缩按钮),避免最后一栏被遮住 */}
          <div style={{ height: 'calc(100vh - 56px - 48px)', overflow: 'auto' }}>
            <Menu
              mode="inline"
              items={menuItems}
              selectedKeys={selectedKeys}
              openKeys={siderCollapsed ? undefined : openKeys}
              onOpenChange={keys => setOpenKeys(keys as string[])}
              onClick={({ key }) => {
                if (typeof key === 'string' && key.startsWith('/')) {
                  navigate(key)
                }
              }}
              style={{ borderRight: 0, paddingTop: 8 }}
            />
          </div>
        </Sider>
        <Content style={{ overflow: 'auto', minHeight: 0 }}>
          <Outlet />
        </Content>
      </Layout>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} pages={palettePages} />
      {/* 全局 Agent 悬浮球(内部在 /agent-harness 路由自行隐藏) */}
      <AgentFloatWidget />
    </Layout>
    </AgentHarnessProvider>
  )
}
